/* eslint-disable max-lines */
import moment from 'moment'
import { Dispatch, GetState } from '~/biz/store/types'
import {
  ActionTypes,
  STRAITSX_TRANSACTIONS_ACTIONS,
  CSV_FORMAT_VERSION,
  TRANSACTIONS_TAB_KEY,
  FilterValuesObject,
  FUNDING_TRANSACTION_TYPE_TAB,
  EXPORT_MODE,
  FilterOptions,
  FilterValues,
  TRANSACTION_TYPE_TAB,
  ApprovalFlow,
  ApprovalFlowRecipient,
  IApprovalFlowResponse,
} from './types'
import { get, ENDPOINTS, post, put } from '~/api'
import transactionTypeFormatter from '~/helpers/transactionTypeHelper'
import { CURRENCY, Moment } from '~/types'
import { Transaction } from '~/biz/store/straitsXTransactions/types'
import {
  getTransactionTypes,
  FUNDING_TX_TYPES,
  SWAP_TX_TYPES,
  OTC_TX_TYPES,
  FIAT_WALLET_TX_TYPES,
} from './helpers'
import { convertToSGTTime, keysToCamel } from '~/helpers/helpers'
import { selectIsLinkedToAsb } from '~/biz/store/accounts/selectors'
import { selectIsFiatWalletOnly } from '~/biz/store/route/selectors'
import { selectFlipperWithId } from '~/store/flippers/selectors'
import { FLIPPER_KEYS } from '~/store/flippers/const'
import { convertKeysToSnakeCase } from '~/api/helpers'
import { APPROVAL_REQUEST_TYPE_APPROVE, APPROVAL_REQUEST_TYPE_REJECT } from '~/common/constants'

const setTransactions = (transactions: Transaction[], contractsExist: boolean) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.GET_TRANSACTIONS,
    transactions,
    contractsExist,
  })
}

const setPendingApprovalFlows = (approvalFlows: ApprovalFlow[], totalCount?: number) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.SET_PENDING_APPROVAL_FLOWS,
    approvalFlows,
    totalCount,
  })
}

const setTransactionCount = (totalCount: number) => (dispatch: Dispatch<ActionTypes>) => {
  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.GET_TRANSACTIONS_TOTAL_COUNT,
    totalCount,
  })
}

const setPendingApprovalFlowsTotalCount = (totalCount: number) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.SET_PENDING_APPROVAL_FLOWS_TOTAL_COUNT,
    totalCount,
  })
}

export const refreshTransactionsData = (resetPage: boolean = true) => (
  dispatch: Dispatch<ActionTypes>
) => {
  if (resetPage) {
    dispatch(resetCurrentPage())
  }
  dispatch(getTransactions())
  dispatch(getTransactionTotalCount())
  dispatch(getPendingApprovalFlowsTotalCount())
}

export const refreshApprovalsData = (resetPage: boolean = true) => (
  dispatch: Dispatch<ActionTypes>
) => {
  if (resetPage) {
    dispatch(resetCurrentPage())
  }
  dispatch(getApprovals())
}

export const getTransactionOptions = () => (
  dispatch: Dispatch<ActionTypes>,
  getState: GetState
) => {
  get(ENDPOINTS.API_XFERS_TRANSACTIONS_FILTER_OPTIONS).then(resp => {
    const isFiatWalletOnly = selectIsFiatWalletOnly(getState())
    const transactionTabOptions = []
    if (
      Object.prototype.hasOwnProperty.call(
        resp.account_types_by_tabs,
        TRANSACTIONS_TAB_KEY.XFERS_ACCOUNT
      )
    ) {
      transactionTabOptions.push(TRANSACTIONS_TAB_KEY.XFERS_ACCOUNT)
    }
    if (
      Object.prototype.hasOwnProperty.call(
        resp.account_types_by_tabs,
        TRANSACTIONS_TAB_KEY.XFERS_WALLET
      )
    ) {
      transactionTabOptions.push(TRANSACTIONS_TAB_KEY.XFERS_WALLET)
    }

    dispatch({
      type: STRAITSX_TRANSACTIONS_ACTIONS.GET_TRANSACTION_TAB_OPTIONS,
      transactionTabOptions,
    })

    dispatch({
      type: STRAITSX_TRANSACTIONS_ACTIONS.SWITCH_TRANSACTIONS_TAB,
      transactionsTabKey: transactionTabOptions[0],
    })

    dispatch({
      type: STRAITSX_TRANSACTIONS_ACTIONS.GET_TRANSACTION_FILTER_OPTIONS,
      viewType: resp.view_type,
      filterOptions: {
        [TRANSACTIONS_TAB_KEY.XFERS_ACCOUNT]: {
          currencySymbols: isFiatWalletOnly
            ? resp.currency_symbols
            : resp.currency_symbols,
          transactionTypes: transactionTypeFormatter({
            initialTypes: resp.transaction_types,
            additionalExcludeList: ['Mint'],
          }),
          transactionStatuses: resp.transaction_statuses,
          sortTypes: resp.sort_types,
          accountTypes: resp.account_types_by_tabs[TRANSACTIONS_TAB_KEY.XFERS_ACCOUNT],
        },
        [TRANSACTIONS_TAB_KEY.XFERS_WALLET]: {
          currencySymbols: isFiatWalletOnly
            ? resp.currency_symbols
            : resp.currency_symbols,
          transactionTypes: transactionTypeFormatter({
            initialTypes: resp.transaction_types,
            additionalExcludeList: ['Mint'],
          }),
          transactionStatuses: resp.transaction_statuses,
          sortTypes: resp.sort_types,
          accountTypes: resp.account_types_by_tabs[TRANSACTIONS_TAB_KEY.XFERS_WALLET],
        },
      },
    })
  })
}

const generateTransactionSubRoute = (params: {
  selectedAccountIds: string[] | number[] | undefined
  isoStartDate: string
  isoEndDate: string
  transactionStatuses: string[]
  transactionTypes: string[]
  sortType: string
  currentPage: number
  pageLimit: number
  searchString: string
  currencySymbols: CURRENCY[]
}) => {
  return `?start_date=${params.isoStartDate}&end_date=${params.isoEndDate}&transaction_statuses=${
    params.transactionStatuses
  }&transaction_types=${params.transactionTypes}&sort_type=${params.sortType}&account_ids=${
    params.selectedAccountIds
  }&current_page=${params.currentPage}&page_limit=${params.pageLimit}&currency_symbols=${
    params.currencySymbols
  }&search_string=${encodeURIComponent(params.searchString)}`
}

export const getTransactions = () => (dispatch: Dispatch<ActionTypes>, getState: GetState) => {
  const currentState = getState().straitsXTransactions
  const {
    transactionsTabKey,
    filterOptions,
    filterValues,
    transactionTypeTab,
    isTransactionTimezoneControlled,
  } = currentState

  if (transactionsTabKey === null) throw new Error('transactionsTabKey cannot be null!')
  if (filterOptions === null) throw new Error('filterOptions cannot be null!')

  const {
    currencySymbols,
    startDate,
    endDate,
    transactionStatuses,
    transactionTypes,
    sortType,
    accountIds,
    currentPage,
    pageLimit,
    searchString,
    isTravelRule,
  } = filterValues[transactionsTabKey]

  const isoStartDate = moment.isMoment(startDate)
    ? parseFilterTime(startDate, 'from', isTransactionTimezoneControlled)
    : ''
  const isoEndDate = moment.isMoment(endDate)
    ? parseFilterTime(endDate, 'to', isTransactionTimezoneControlled)
    : ''
  const isFiatWalletOnly = selectIsFiatWalletOnly(getState())

  const { accountTypes } = filterOptions[transactionsTabKey]
  const defaultAccountIds = accountTypes && accountTypes.map(element => element.value)
  // Note: If user haven't filtered by wallet type, use the account ids given by default for the particular tab
  const selectedAccountIds = accountIds.length <= 0 ? defaultAccountIds : accountIds

  const generatedSubRoute = generateTransactionSubRoute({
    currentPage,
    isoStartDate,
    isoEndDate,
    pageLimit,
    searchString,
    selectedAccountIds,
    sortType,
    transactionStatuses,
    transactionTypes: getTransactionTypes(transactionTypeTab, transactionTypes, isFiatWalletOnly), // NOTE: add transaction type filter base on the transaction type tab (funding, swap)
    currencySymbols,
  })

  const subRoute = generatedSubRoute + `&is_travel_rule=${isTravelRule}`

  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.SET_IS_TXN_LOADING,
    isTxnLoading: true,
  })

  get(ENDPOINTS.API_XFERS_TRANSACTIONS, subRoute).then(resp => {
    const transactions = (resp.transactions as unknown[]).map(i => keysToCamel(i) as Transaction)
    dispatch(setTransactions(transactions, resp.contracts_exist))
  })
}

export const getApprovals = () => (dispatch: Dispatch<ActionTypes>, getState: GetState) => {
  const currentState = getState().straitsXTransactions
  const {
    transactionsTabKey,
    filterOptions,
    filterValues,
    transactionTypeTab,
    isTransactionTimezoneControlled,
  } = currentState

  if (transactionsTabKey === null) throw new Error('transactionsTabKey cannot be null!')
  if (filterOptions === null) throw new Error('filterOptions cannot be null!')

  const {
    currencySymbols,
    startDate,
    endDate,
    transactionStatuses,
    transactionTypes,
    sortType,
    accountIds,
    currentPage,
    pageLimit,
    searchString,
  } = filterValues[transactionsTabKey]

  const isoStartDate = moment.isMoment(startDate)
    ? parseFilterTime(startDate, 'from', isTransactionTimezoneControlled)
    : ''
  const isoEndDate = moment.isMoment(endDate)
    ? parseFilterTime(endDate, 'to', isTransactionTimezoneControlled)
    : ''
  const isFiatWalletOnly = selectIsFiatWalletOnly(getState())

  const { accountTypes } = filterOptions[transactionsTabKey]
  const defaultAccountIds = accountTypes && accountTypes.map(element => element.value)
  // Note: If user haven't filtered by wallet type, use the account ids given by default for the particular tab
  const selectedAccountIds = accountIds.length <= 0 ? defaultAccountIds : accountIds

  const generatedSubRoute = generateTransactionSubRoute({
    currentPage,
    isoStartDate,
    isoEndDate,
    pageLimit,
    searchString,
    selectedAccountIds,
    sortType,
    transactionStatuses,
    transactionTypes: getTransactionTypes(transactionTypeTab, transactionTypes, isFiatWalletOnly), // NOTE: add transaction type filter base on the transaction type tab (funding, swap)
    currencySymbols,
  })

  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.SET_IS_TXN_LOADING,
    isTxnLoading: true,
  })

  get(ENDPOINTS.API_APPROVAL_REQUEST, generatedSubRoute)
    .then((resp: IApprovalFlowResponse) => {
      const approvalFlows = resp.transactions.map(t => {
        const approvalFlow = keysToCamel(t) as ApprovalFlow

        approvalFlow.recipient = keysToCamel(t.recipient) as ApprovalFlowRecipient

        return approvalFlow
      })

      dispatch(setPendingApprovalFlows(approvalFlows, resp.total_count))
    })
    .catch(() => {
      dispatch(setPendingApprovalFlows([], 0))
    })
}

export const getTransactionTotalCount = () => (
  dispatch: Dispatch<ActionTypes>,
  getState: GetState
) => {
  const {
    transactionsTabKey,
    filterOptions,
    transactionTypeTab,
    isTransactionTimezoneControlled,
  } = getState().straitsXTransactions

  if (transactionsTabKey === null) throw new Error('transactionsTabKey cannot be null!')
  if (filterOptions === null) throw new Error('filterOptions cannot be null!')

  const {
    currencySymbols,
    startDate,
    endDate,
    transactionStatuses,
    transactionTypes,
    sortType,
    accountIds,
    currentPage,
    pageLimit,
    searchString,
  } = getState().straitsXTransactions.filterValues[transactionsTabKey]

  const isoStartDate = moment.isMoment(startDate)
    ? parseFilterTime(startDate, 'from', isTransactionTimezoneControlled)
    : ''
  const isoEndDate = moment.isMoment(endDate)
    ? parseFilterTime(endDate, 'to', isTransactionTimezoneControlled)
    : ''
  const isFiatWalletOnly = selectIsFiatWalletOnly(getState())

  const { accountTypes } = filterOptions[transactionsTabKey]
  const defaultAccountIds = accountTypes && accountTypes.map(element => element.value)
  // Note: If user haven't filtered by wallet type, use the account ids given by default for the particular tab
  const selectedAccountIds = accountIds.length <= 0 ? defaultAccountIds : accountIds

  const subRoute = generateTransactionSubRoute({
    currentPage,
    isoStartDate,
    isoEndDate,
    pageLimit,
    searchString,
    selectedAccountIds,
    sortType,
    transactionStatuses,
    transactionTypes: getTransactionTypes(transactionTypeTab, transactionTypes, isFiatWalletOnly), // NOTE: add transaction type filter base on the transaction type tab (funding, swap)
    currencySymbols,
  })

  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.SET_IS_PAGINATION_LOADING,
    isPaginationLoading: true,
  })

  get(ENDPOINTS.API_XFERS_TRANSACTIONS_TOTAL_COUNT, subRoute).then(resp => {
    dispatch(setTransactionCount(resp.total_count))
  })
}

export const getPendingApprovalFlowsTotalCount = () => (dispatch: Dispatch<ActionTypes>) => {
  get(ENDPOINTS.API_APPROVAL_FLOWS_TOTAL_COUNT).then(resp => {
    dispatch(setPendingApprovalFlowsTotalCount(resp.total_count))
  })
}

export const setFilters = (filterValuesObject: FilterValuesObject) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.SET_FILTERS,
    filterValuesObject,
  })

  let resetCurrentPage = true
  if ('currentPage' in filterValuesObject) {
    resetCurrentPage = false
  }
  dispatch(refreshTransactions(resetCurrentPage))
}

export const resetCurrentPage = () => (dispatch: Dispatch<ActionTypes>) => {
  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.RESET_CURRENT_PAGE,
  })
}

export const setFiltersWithoutRefreshTransactions = (filterValuesObject: FilterValuesObject) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.SET_FILTERS,
    filterValuesObject,
  })
}

export const getCsvFormatVersion = () => (dispatch: Dispatch<ActionTypes>) => {
  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.SET_CSV_FORMAT_VERSION,
    csvFormatVersion: CSV_FORMAT_VERSION.V3,
  })
}

export const switchTransactionsTab = (transactionsTabKey: TRANSACTIONS_TAB_KEY) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.SWITCH_TRANSACTIONS_TAB,
    transactionsTabKey,
  })

  dispatch(refreshTransactions())
}

export const setTransactionTypeTab = (tab: TRANSACTION_TYPE_TAB) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.SET_TRANSACTION_TYPE_TAB,
    transactionTypeTab: tab,
  })
}

export const onSwitchingTransactionTypeTab = (tab: TRANSACTION_TYPE_TAB) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch(setTransactionTypeTab(tab))

  dispatch(setFundingTransactionTypeTab(FUNDING_TRANSACTION_TYPE_TAB.ALL_FUNDING))
  dispatch(setFiltersWithoutRefreshTransactions({ transactionTypes: [] }))
  dispatch(setFiltersWithoutRefreshTransactions({ transactionStatuses: [] }))
  dispatch(setFiltersWithoutRefreshTransactions({ isTravelRule: false }))

  // reset data before refreshing
  dispatch(setTransactions([], false))
  dispatch(setTransactionCount(0))
  dispatch(setPendingApprovalFlows([]))

  dispatch(refreshTransactions())
}

const setFundingTransactionTypeTab = (tab: FUNDING_TRANSACTION_TYPE_TAB) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.SET_FUNDING_TRANSACTION_TYPE_TAB,
    fundingTransactionTypeTab: tab,
  })
}

export const onSwitchingFundingTransactionTypeTab = (tab: FUNDING_TRANSACTION_TYPE_TAB) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch(setFundingTransactionTypeTab(tab))

  if (tab === FUNDING_TRANSACTION_TYPE_TAB.RECEIVE_DEPOSIT) {
    dispatch(setFiltersWithoutRefreshTransactions({ transactionTypes: 'stablecoin_deposit' }))
    dispatch(setFiltersWithoutRefreshTransactions({ transactionStatuses: 'pending' }))
    dispatch(setFiltersWithoutRefreshTransactions({ isTravelRule: true }))
  } else {
    dispatch(setFiltersWithoutRefreshTransactions({ transactionTypes: [] }))
    dispatch(setFiltersWithoutRefreshTransactions({ transactionStatuses: [] }))
    dispatch(setFiltersWithoutRefreshTransactions({ isTravelRule: false }))
  }

  // reset data before refreshing
  dispatch(setTransactions([], false))
  dispatch(setTransactionCount(0))
  dispatch(setPendingApprovalFlows([]))
  dispatch(refreshTransactions())
}

// refreshTransactions function will refresh the transactions data
// based on the current fundingTransactionTypeTab, either pending
// approvals or other transactions
const refreshTransactions = (resetPage: boolean = true) => (
  dispatch: Dispatch<ActionTypes>,
  getState: GetState
) => {
  const { fundingTransactionTypeTab } = getState().straitsXTransactions
  if (fundingTransactionTypeTab === FUNDING_TRANSACTION_TYPE_TAB.APPROVE_TRANSACTIONS) {
    dispatch(refreshApprovalsData(resetPage))
  } else {
    dispatch(refreshTransactionsData(resetPage))
  }
}

export const exportCSV = (
  exportMode: EXPORT_MODE | undefined,
  filterOptions: FilterOptions,
  filterValues: FilterValues,
  isTimezoneControlled: boolean,
  onSuccess?: () => void
) => {
  const { accountTypes } = filterOptions
  const {
    startDate,
    endDate,
    accountIds,
    transactionStatuses,
    transactionTypes,
    searchString,
    sortType,
    currencySymbols,
  } = filterValues

  // get tx types base on export mode
  let finalTxTypes = transactionTypes
  if (exportMode === EXPORT_MODE.ALL) {
    finalTxTypes = []
  } else if (exportMode === EXPORT_MODE.SWAP) {
    finalTxTypes = getTransactionTypes(TRANSACTION_TYPE_TAB.SWAP, transactionTypes)
  } else if (exportMode === EXPORT_MODE.FUNDING) {
    finalTxTypes = getTransactionTypes(TRANSACTION_TYPE_TAB.FUNDING, transactionTypes)
  }

  const isoStartDate = moment.isMoment(startDate)
    ? parseFilterTime(startDate, 'from', isTimezoneControlled)
    : ''
  const isoEndDate = moment.isMoment(endDate)
    ? parseFilterTime(endDate, 'to', isTimezoneControlled)
    : ''

  const defaultAccountIds = accountTypes && accountTypes.map(element => element.value)
  // Note: If user haven't filtered by wallet type, use the account ids given by default for the particular tab
  const selectedAccountIds = accountIds.length <= 0 ? defaultAccountIds : accountIds

  const requestBody = {
    currency_symbols: currencySymbols,
    start_date: isoStartDate,
    end_date: isoEndDate,
    transaction_statuses: transactionStatuses.toString(),
    transaction_types: finalTxTypes.toString(),
    sort_type: sortType.toString(),
    account_ids: selectedAccountIds ? selectedAccountIds.toString() : '',
    search_string: searchString,
    csv_type: 'only_completed',
    // Note: v4 format not finalized yet, we use v3 for now
    csv_format_version: 'V3',
  }

  post(ENDPOINTS.API_XFERS_TRANSACTIONS_EXPORT_CSV, requestBody).then(() => {
    if (onSuccess) {
      onSuccess()
    }
  })
}

export const parseFilterTime = (
  input: Moment,
  type: 'from' | 'to',
  isTimezoneControlEnabled: boolean
) => {
  if (isTimezoneControlEnabled) {
    return convertToSGTTime(input, type)
  }

  if (type === 'from') {
    return input.startOf('day').toISOString()
  } else {
    return input.endOf('day').toISOString()
  }
}

export const getTotalTravelRuleComplianceBlockerCount = () => (dispatch: Dispatch<ActionTypes>) => {
  const subRoute = `?transaction_types=stablecoin_deposit`
  get(ENDPOINTS.API_XFERS_TRANSACTIONS_TRAVEL_RULE_COMPLIANCE_BLOCKER_TOTAL_COUNT, subRoute).then(
    resp => {
      dispatch({
        type: STRAITSX_TRANSACTIONS_ACTIONS.GET_TRAVEL_RULE_COMPLIANCE_BLOCKER_TOTAL_COUNT,
        travelRuleComplianceBlockerTotalCount: resp.total_count,
      })
    }
  )
}

export const setIsTransactionTimezoneControlled = (isEnabled: boolean) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: STRAITSX_TRANSACTIONS_ACTIONS.SET_IS_TRANSACTION_TIMEZONE_CONTROLLED,
    isTransactionTimezoneControlled: isEnabled,
  })
}

export const getLatestTransactions = () => (
  dispatch: Dispatch<ActionTypes>,
  getState: GetState
) => {
  const { email } = getState().route.user
  const isLinkedToAsb = selectIsLinkedToAsb(getState())
  const flipperOtcTrxUI = selectFlipperWithId(
    FLIPPER_KEYS.SG_OTC_TRX_HISTORY_BIZ_DASHBOARD,
    email
  )(getState())
  const isFiatWalletOnly = selectIsFiatWalletOnly(getState())
  let latestTxTypes = isFiatWalletOnly
    ? [...FIAT_WALLET_TX_TYPES]
    : [...FUNDING_TX_TYPES, ...SWAP_TX_TYPES]

  if (isLinkedToAsb && flipperOtcTrxUI?.enabled) latestTxTypes = [...latestTxTypes, ...OTC_TX_TYPES]

  get(
    ENDPOINTS.API_XFERS_TRANSACTIONS,
    `?transaction_types=${latestTxTypes}&current_page=1&page_limit=5`
  ).then(txnResp => {
    const transactions = (txnResp.transactions as unknown[]).map(i => keysToCamel(i) as Transaction)

    dispatch({
      type: STRAITSX_TRANSACTIONS_ACTIONS.SET_LATEST_TRANSACTIONS,
      latestTransactions: transactions,
    })
  })
}

export const requestApproval = (
  transactionId: number,
  approvalReason: string,
  onSuccess?: () => void,
  onFailure?: () => void
) => {
  sendApprovalRequest(
    transactionId,
    APPROVAL_REQUEST_TYPE_APPROVE,
    approvalReason,
    onSuccess,
    onFailure
  )
}

export const requestRejection = (
  transactionId: number,
  rejectionReason: string,
  onSuccess?: () => void,
  onFailure?: () => void
) => {
  sendApprovalRequest(
    transactionId,
    APPROVAL_REQUEST_TYPE_REJECT,
    rejectionReason,
    onSuccess,
    onFailure
  )
}

const sendApprovalRequest = (
  transactionId: number,
  actionType: string,
  actionReason: string,
  onSuccess?: () => void,
  onFailure?: () => void
) => {
  put(
    ENDPOINTS.API_APPROVAL_REQUEST + '/' + transactionId,
    convertKeysToSnakeCase({
      approvalAction: actionType,
      actionReason,
    })
  )
    .then(() => {
      if (onSuccess) onSuccess()
    })
    .catch(() => {
      if (onFailure) onFailure()
    })
}
